import React from 'react'
import { Pill } from '../../../../../shared/sso-workspace/pill'
import { LocalTime } from '../../../../../shared/date/local-time-view'
import { agoText } from '../../../../../shared/date/local-time'
import { formatAgo, formatCount, formatDateTime } from '../../../../../shared/sso-workspace/format'
import { ErrorReference } from '../../../../../shared/error/error-reference'
import { errorCodeLabel } from '../../../../unauthorized/auth/page/login-error'
import { checkView, failureReasonText } from '../signin-method-words'

// A method's health (design §5): the last successful sign-in through it
// (auth_claims on its anchor) and how many in the last 7 and 30 days; the
// last failure with its time (the viewer's own), reason, the identity
// provider's error number and the failed request's reference (the Request
// log for a super admin: ErrorReference); and the kind's check.
//
// check: the last check this page ran (or the one a refused switch-on
// answered with), null before any. checking: one is running. checkError /
// checkFailure: the check itself couldn't run.
export const MethodHealth = ({ method, label, check, checking, checkError, checkFailure, onCheck }) => {
  const health = method.setUp ? method.health : null
  const failed = Boolean(health && health.lastFailureAt)

  return (
    <div className="d-grid gap-3">
      {!method.setUp && <p className="sw-hint m-0">It isn't set up, so it has no sign-ins yet.</p>}
      {method.setUp && !health && <p className="sw-hint m-0">Its sign-ins couldn't be read. Reload to try again.</p>}
      {health &&
        <dl className="row g-2 m-0" data-testid="method-health">
          <dt className="col-sm-4 sw-label">Last successful sign-in</dt>
          <dd className="col-sm-8 m-0" data-testid="health-last-sign-in">
            {health.lastSignInAt
              ? <><LocalTime value={health.lastSignInAt} /> <span className="sw-hint">({agoText(health.lastSignInAt)})</span></>
              : <span className="sw-hint">No sign-ins yet</span>}
          </dd>
          <dt className="col-sm-4 sw-label">Sign-ins</dt>
          <dd className="col-sm-8 m-0" data-testid="health-counts">
            {formatCount(health.signIns7d)} in the last 7 days · {formatCount(health.signIns30d)} in the last 30 days
          </dd>
          <dt className="col-sm-4 sw-label">Last failure</dt>
          <dd className="col-sm-8 m-0" data-testid="health-last-failure">
            {failed
              ? <div className="d-grid gap-1">
                <span><LocalTime value={health.lastFailureAt} className="fw-semibold" /> <span className="sw-hint">({agoText(health.lastFailureAt)})</span></span>
                <span>{failureReasonText(health.lastFailureReason, label)}</span>
                {health.lastFailureIdpCode &&
                  <span>{errorCodeLabel(label)}: <code className="sw-mono">{health.lastFailureIdpCode}</code></span>}
                {health.lastFailureRef && <ErrorReference requestID={health.lastFailureRef} at={health.lastFailureAt} compact />}
              </div>
              : <span className="sw-hint">None recorded</span>}
          </dd>
        </dl>}
      {health &&
        <p className="sw-hint m-0">
          A failure is recorded when the identity provider, the sign-in exchange, its token or the credential fails, never for a cancelled sign-in or one the sign-in rules refused, and at most once a minute.
        </p>}

      <div className="d-grid gap-2" data-testid="method-check">
        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2">
          <div className="min-w-0">
            <h3 className="h6 fw-semibold m-0">Check</h3>
            <p className="sw-hint m-0">Checks that its configuration works, without anyone signing in.</p>
          </div>
          <button type="button" className="btn sw-btn" disabled={checking} onClick={onCheck}>{checking ? 'Checking…' : 'Check'}</button>
        </div>
        {checkError && <p className="text-danger small m-0" role="alert">{checkError}</p>}
        {checkError && <ErrorReference error={checkFailure} />}
        {check && <CheckResult check={check} />}
      </div>
    </div>
  )
}

// The check's answer: a pill, auth's summary and fix, and what it was about.
// The workspace's credential-check look (sw-credcheck).
export const CheckResult = ({ check }) => {
  const view = checkView(check)
  return (
    <div className={`sw-credcheck ${view.tone || ''}`.trim()} data-testid="method-check-result" data-status={check.status} role="status">
      <div className="sw-credcheck-head">
        <span className="sw-credcheck-title">Check</span>
        <Pill tone={view.tone}>{view.label}</Pill>
      </div>
      {check.summary && <p className="m-0">{check.summary}</p>}
      {check.fix && <p className="m-0"><span className="fw-semibold">To fix: </span>{check.fix}</p>}
      {(check.thumbprint || check.code || check.checkedAt) &&
        <p className="sw-credcheck-meta m-0">
          {check.thumbprint && <span>Certificate thumbprint (SHA-1) <code className="sw-mono">{check.thumbprint}</code></span>}
          {check.code && <span>Answer <code className="sw-mono">{check.code}</code></span>}
          {check.checkedAt && <span title={formatDateTime(check.checkedAt)}>Checked {formatAgo(check.checkedAt)}</span>}
        </p>}
    </div>
  )
}
